/**
 * Bikini Set Selector v2.0
 *
 * Hoofdproduct = "set" landing page (wordt zelf NIET besteld).
 * Twee gekoppelde producten (top + broekje) hebben elk een eigen
 * maatkeuze. Klant kan een of beide aan winkelwagen toevoegen.
 *
 * Features:
 *  - Eigen maatkeuze per artikel
 *  - Voorraadcontrole (variant.available)
 *  - Korting per artikel (compare_at_price)
 *  - Validatie: gekozen artikel moet maat hebben
 *  - Hoofdproduct wordt nooit aan cart toegevoegd
 */

class BikiniSetSelector {
  constructor(element) {
    this.container = element;
    this.items = []; // [{ role, handle, title, container, sizeOptionsEl, priceEl, errorEl, stockEl, stockTextEl, variants, selectedVariant }]
    this.totalPriceEl = element.querySelector('[data-total-price]');
    this.totalCompareEl = element.querySelector('[data-total-compare]');
    this.ctaButton = element.querySelector('[data-bikini-add-to-cart]');
    this.hintEl = element.querySelector('[data-bikini-hint]');
    this.errorEl = element.querySelector('[data-bikini-error]');

    this.moneyFormat = window.Shopify && window.Shopify.currency && window.Shopify.currency.active
      ? window.Shopify.currency.active
      : 'EUR';

    this.init();
  }

  async init() {
    const itemElements = this.container.querySelectorAll('[data-set-item]');
    this.items = Array.from(itemElements).map((el) => ({
      role: el.dataset.role,
      handle: el.dataset.productHandle,
      productId: el.dataset.productId,
      title: el.dataset.productTitle || '',
      container: el,
      sizeOptionsEl: el.querySelector('[data-size-options]'),
      priceEl: el.querySelector('[data-item-price]'),
      errorEl: el.querySelector('[data-item-error]'),
      stockEl: el.querySelector('[data-item-stock]'),
      stockTextEl: el.querySelector('[data-item-stock-text]'),
      variants: this.parseVariants(el),
      selectedVariant: null,
    }));

    this.disableHostProductForm();
    this.bindCtaClick();

    this.items.forEach((item) => this.renderItem(item));

    this.updateUi();
  }

  parseVariants(itemEl) {
    const raw = itemEl.dataset.productVariants;
    if (!raw) return [];
    try {
      return JSON.parse(raw);
    } catch (err) {
      console.error('Bikini set selector: kon variant-data niet parsen', err);
      return [];
    }
  }

  /**
   * Het hoofdproduct (set) is geen losse aankoop. We verbergen daarom de
   * standaard buy-buttons (Add to cart + accelerated checkout) en gebruiken
   * onze eigen CTA. Een form-submit elders op de pagina onderscheppen we
   * alsnog als veiligheidsnet.
   */
  disableHostProductForm() {
    document.querySelectorAll('.buy-buttons-block').forEach((el) => {
      el.classList.add('bikini-set-hidden-host');
    });

    // Variant-picker van het hoofdproduct slaat nergens op: het hoofdproduct
    // wordt niet besteld en de gekoppelde producten hebben hun eigen maatkeuze.
    document.querySelectorAll('variant-picker, .variant-picker').forEach((el) => {
      if (el.closest('.bikini-set-selector')) return;
      el.classList.add('bikini-set-hidden-host');
    });

    // De voorraadmelding van het hoofd-set-product slaat nergens op (het
    // wordt nooit besteld). Voorraad per top/broekje hebben we zelf, per item.
    // Pas alleen toe op de host-melding, niet op onze eigen indicators.
    document.querySelectorAll('#product-inventory-status, .product-inventory-status').forEach((el) => {
      if (el.closest('.bikini-set-selector')) return;
      el.classList.add('bikini-set-hidden-host');
    });

    // Alléén de host-PDP-form onderscheppen, niet quick-add of andere
    // /cart/add forms elders op de pagina.
    this.hostForm = document.querySelector('.buy-buttons-block form[action*="/cart/add"]');
    if (this.hostForm) {
      this.hostForm.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.handleAddToCart();
      }, true);
    }
  }

  bindCtaClick() {
    if (this.ctaButton) {
      this.ctaButton.addEventListener('click', () => this.handleAddToCart());
    }
  }

  renderItem(item) {
    if (!item.variants || item.variants.length === 0) {
      this.renderItemError(item, 'Dit artikel heeft geen varianten.');
      this.updateItemStock(item);
      return;
    }

    this.renderSizeOptions(item);
    this.renderItemPrice(item);
    this.updateItemStock(item);
  }

  renderSizeOptions(item) {
    item.sizeOptionsEl.innerHTML = '';

    // Eén default variant (geen maat-optie) → direct selecteren.
    const onlyDefault = item.variants.length === 1
      && (item.variants[0].title === 'Default Title' || !item.variants[0].option1);

    if (onlyDefault) {
      item.selectedVariant = item.variants[0];
      const note = document.createElement('div');
      note.className = 'bikini-set-selector__no-sizes';
      note.textContent = 'Geen maat nodig';
      item.sizeOptionsEl.appendChild(note);
      return;
    }

    item.variants.forEach((variant) => {
      const sizeLabel = this.extractSizeLabel(variant);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bikini-set-selector__size-button';
      button.dataset.variantId = String(variant.id);
      button.textContent = sizeLabel;
      if (variant.available) {
        button.addEventListener('click', () => this.selectVariant(item, variant, button));
      } else {
        button.classList.add('is-sold-out');
        button.setAttribute('aria-label', `${sizeLabel} (uitverkocht — klik om een seintje te krijgen)`);
        button.title = 'Uitverkocht — laat je waarschuwen wanneer terug op voorraad';
        button.addEventListener('click', () => this.openNotifyMe(item, variant));
      }
      item.sizeOptionsEl.appendChild(button);
    });

    // Voeg een hint toe wanneer het hele item uitverkocht is, zodat duidelijk is
    // dat de set-CTA niet werkt maar notify-me wel.
    const hasAnyAvailable = item.variants.some((v) => v.available);
    if (!hasAnyAvailable) {
      const hint = document.createElement('p');
      hint.className = 'bikini-set-selector__sold-out-hint';
      hint.textContent = 'Klik op een maat om een seintje te krijgen zodra hij terug is.';
      item.sizeOptionsEl.insertAdjacentElement('afterend', hint);
    }
  }

  /**
   * Open de notify-me modal voor een gekoppeld product/variant. Werkt
   * onafhankelijk van de host-variant-picker: we dispatchen een
   * variant:update event zodat het notify-me-form-component zijn
   * interne state + form-velden (return_to, note) bijwerkt.
   */
  openNotifyMe(item, variant) {
    const dialogId = `NotifyMeDialog-${item.productId}`;
    const dialog = document.getElementById(dialogId);
    if (!dialog) {
      console.warn('Bikini set selector: notify-me dialog niet gevonden voor', item.title);
      return;
    }

    // Reset zichtbare state (mocht het modal eerder open zijn geweest).
    dialog.querySelectorAll('[data-notify-me-error], [data-notify-me-success]')
      .forEach((el) => el.classList.add('hidden'));
    dialog.querySelectorAll('.notify-me-form__fields, .notify-me-form__submit')
      .forEach((el) => { el.style.display = ''; });
    const submitBtn = dialog.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = false;
      if (submitBtn.dataset.originalText) {
        submitBtn.textContent = submitBtn.dataset.originalText;
        delete submitBtn.dataset.originalText;
      }
    }

    if (variant) {
      document.dispatchEvent(new CustomEvent('variant:update', {
        bubbles: true,
        detail: {
          resource: {
            id: variant.id,
            title: variant.title,
            available: variant.available,
            sku: variant.sku || '',
          },
          sourceId: 'bikini-set-selector',
          data: { productId: String(item.productId) },
        },
      }));
    }

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }

  extractSizeLabel(variant) {
    if (variant.option1) return variant.option1;
    if (variant.title && variant.title.includes(' / ')) {
      return variant.title.split(' / ')[0].trim();
    }
    return variant.title || '';
  }

  selectVariant(item, variant, buttonEl) {
    if (!variant.available) return;

    item.selectedVariant = variant;

    item.sizeOptionsEl
      .querySelectorAll('.bikini-set-selector__size-button')
      .forEach((btn) => btn.classList.remove('selected'));
    buttonEl.classList.add('selected');

    if (item.errorEl) item.errorEl.hidden = true;

    this.renderItemPrice(item);
    this.updateItemStock(item);
    this.updateUi();
  }

  renderItemPrice(item) {
    if (!item.priceEl) return;

    const variant = item.selectedVariant
      || item.variants.find((v) => v.available)
      || item.variants[0];
    if (!variant) return;

    const price = variant.price;
    const compareAt = variant.compare_at_price;

    let html = '';
    if (compareAt && compareAt > price) {
      html += `<span class="bikini-set-selector__price--compare">${this.formatPrice(compareAt)}</span>`;
    }
    html += `<span class="bikini-set-selector__price--current">${this.formatPrice(price)}</span>`;
    item.priceEl.innerHTML = html;
  }

  /**
   * Voorraadtekst per gekoppeld product:
   *  - Geen maat gekozen: samenvatting (op voorraad / beperkt / uitverkocht)
   *  - Maat gekozen: variant-specifiek (incl. exact aantal bij low stock)
   */
  updateItemStock(item) {
    if (!item.stockEl || !item.stockTextEl) return;

    item.stockEl.classList.remove('in-stock', 'low-stock', 'out-of-stock');

    const variants = item.variants || [];
    const selected = item.selectedVariant;

    if (variants.length === 0) {
      item.stockEl.classList.add('out-of-stock');
      item.stockTextEl.textContent = 'Niet beschikbaar';
      return;
    }

    if (selected) {
      const tracked = selected.inventory_management;
      const qty = parseInt(selected.inventory_quantity, 10) || 0;

      if (!selected.available) {
        item.stockEl.classList.add('out-of-stock');
        item.stockTextEl.textContent = 'Niet op voorraad in deze maat';
      } else if (tracked && qty === 1) {
        item.stockEl.classList.add('low-stock');
        item.stockTextEl.textContent = 'Nog maar 1 op voorraad';
      } else if (tracked && qty > 1 && qty <= 10) {
        item.stockEl.classList.add('low-stock');
        item.stockTextEl.textContent = `Let op! Nog maar ${qty} op voorraad`;
      } else {
        item.stockEl.classList.add('in-stock');
        item.stockTextEl.textContent = 'Op voorraad';
      }
      return;
    }

    // Geen variant geselecteerd: samenvatting over alle varianten.
    const availableVariants = variants.filter((v) => v.available);
    if (availableVariants.length === 0) {
      item.stockEl.classList.add('out-of-stock');
      item.stockTextEl.textContent = 'Momenteel niet op voorraad';
      return;
    }

    const someSoldOut = availableVariants.length < variants.length;

    // "Onbeperkt" = beschikbaar én niet getrackt → effectief altijd op voorraad.
    // We kijken alleen naar getrackte varianten voor low-stock conclusies.
    const trackedAvailable = availableVariants.filter((v) => v.inventory_management);
    const untrackedAvailable = availableVariants.length - trackedAvailable.length;
    const lowTracked = trackedAvailable.filter((v) => (parseInt(v.inventory_quantity, 10) || 0) <= 10);

    const allAvailableAreLow = untrackedAvailable === 0
      && trackedAvailable.length > 0
      && lowTracked.length === trackedAvailable.length;
    const someLow = lowTracked.length > 0;

    if (allAvailableAreLow) {
      item.stockEl.classList.add('low-stock');
      item.stockTextEl.textContent = someSoldOut
        ? 'Beperkt op voorraad, sommige maten uitverkocht'
        : 'Beperkt op voorraad';
    } else if (someSoldOut && someLow) {
      item.stockEl.classList.add('low-stock');
      item.stockTextEl.textContent = 'Sommige maten uitverkocht of beperkt beschikbaar';
    } else if (someSoldOut) {
      item.stockEl.classList.add('low-stock');
      item.stockTextEl.textContent = 'Sommige maten uitverkocht';
    } else if (someLow) {
      item.stockEl.classList.add('low-stock');
      item.stockTextEl.textContent = 'Sommige maten beperkt beschikbaar';
    } else {
      item.stockEl.classList.add('in-stock');
      item.stockTextEl.textContent = 'Op voorraad';
    }
  }

  renderItemError(item, message) {
    if (item.sizeOptionsEl) {
      item.sizeOptionsEl.innerHTML = `
        <div class="bikini-set-selector__item-load-error">${message}</div>
      `;
    }
  }

  updateUi() {
    const selectedItems = this.items.filter((i) => i.selectedVariant);

    // Totaalprijs berekenen op basis van actuele variant.price (incl. korting).
    const totalPrice = selectedItems.reduce((sum, i) => sum + (i.selectedVariant.price || 0), 0);
    const totalCompare = selectedItems.reduce((sum, i) => {
      const compare = i.selectedVariant.compare_at_price && i.selectedVariant.compare_at_price > i.selectedVariant.price
        ? i.selectedVariant.compare_at_price
        : i.selectedVariant.price;
      return sum + (compare || 0);
    }, 0);

    if (this.totalPriceEl) {
      this.totalPriceEl.textContent = this.formatPrice(totalPrice);
    }
    if (this.totalCompareEl) {
      if (totalCompare > totalPrice) {
        this.totalCompareEl.textContent = this.formatPrice(totalCompare);
        this.totalCompareEl.hidden = false;
      } else {
        this.totalCompareEl.hidden = true;
      }
    }

    const hasSelection = selectedItems.length > 0;
    const totalItems = this.items.length;
    if (this.ctaButton) {
      this.ctaButton.disabled = !hasSelection;
      if (totalItems > 1 && selectedItems.length === totalItems) {
        this.ctaButton.textContent = 'Voeg complete set toe';
      } else if (selectedItems.length === 1) {
        this.ctaButton.textContent = `Voeg ${selectedItems[0].title || 'artikel'} toe`;
      } else if (selectedItems.length > 1) {
        this.ctaButton.textContent = `Voeg ${selectedItems.length} artikelen toe`;
      } else {
        this.ctaButton.textContent = 'Maak een selectie';
      }
    }
    if (this.hintEl) {
      if (!hasSelection) {
        this.hintEl.textContent = 'Selecteer minimaal één maat om door te gaan.';
        this.hintEl.hidden = false;
      } else {
        this.hintEl.hidden = true;
      }
    }
  }

  async handleAddToCart() {
    this.clearError();

    const selectedItems = this.items.filter((i) => i.selectedVariant);

    if (selectedItems.length === 0) {
      // Markeer alle items zonder selectie zodat de klant ziet waar de
      // ontbrekende maat zit.
      this.items
        .filter((i) => !i.selectedVariant && i.variants && i.variants.length > 0)
        .forEach((i) => {
          if (i.errorEl) i.errorEl.hidden = false;
        });
      this.showError('Selecteer minimaal één artikel met maat.');
      return;
    }

    // Voorraadcheck per gekozen variant (mocht client-side data verouderd zijn,
    // dan vangt /cart/add.js het serverseits af; we checken alvast hier voor
    // een snellere foutmelding).
    const unavailable = selectedItems.find((i) => !i.selectedVariant.available);
    if (unavailable) {
      this.showError(`${unavailable.title || 'Een artikel'} is niet meer op voorraad in de gekozen maat.`);
      return;
    }

    this.setLoading(true);

    const items = selectedItems.map((i) => ({
      id: parseInt(i.selectedVariant.id, 10),
      quantity: 1,
    }));

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.description || 'Toevoegen aan winkelwagen mislukt.');
      }

      await this.refreshCart();
      this.setLoading(false);
      this.showSuccess(selectedItems.length);
    } catch (err) {
      console.error('Bikini set selector: add-to-cart fout', err);
      this.setLoading(false);
      this.showError(err.message || 'Er is iets misgegaan, probeer het opnieuw.');
    }
  }

  setLoading(isLoading) {
    if (!this.ctaButton) return;
    this.ctaButton.classList.toggle('loading', isLoading);
    this.ctaButton.disabled = isLoading;
    if (isLoading) {
      this.ctaButton.dataset.busyText = this.ctaButton.textContent;
      this.ctaButton.textContent = 'Bezig met toevoegen...';
    } else if (this.ctaButton.dataset.busyText) {
      this.ctaButton.textContent = this.ctaButton.dataset.busyText;
      delete this.ctaButton.dataset.busyText;
    }
  }

  showError(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    } else {
      alert(message);
    }
  }

  clearError() {
    if (this.errorEl) {
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
    this.items.forEach((i) => {
      if (i.errorEl) i.errorEl.hidden = true;
    });
  }

  showSuccess(count) {
    const original = this.ctaButton ? this.ctaButton.textContent : '';
    const totalItems = this.items.length;
    if (this.ctaButton) {
      this.ctaButton.textContent = totalItems > 1 && count === totalItems
        ? '✓ Set toegevoegd aan winkelwagen'
        : '✓ Toegevoegd aan winkelwagen';
      this.ctaButton.classList.add('success');
      setTimeout(() => {
        this.ctaButton.textContent = original;
        this.ctaButton.classList.remove('success');
      }, 2200);
    }
  }

  async refreshCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'bikini-set-selector',
          data: { source: 'bikini-set-selector', itemCount: cart.item_count },
        },
      }));

      document.querySelectorAll('[data-cart-count], .cart-count, #cart-count').forEach((el) => {
        el.textContent = cart.item_count;
      });
      document.querySelectorAll('.cart-icon__badge, [data-cart-badge]').forEach((el) => {
        el.textContent = cart.item_count;
        if (cart.item_count > 0) el.style.display = 'flex';
      });
    } catch (err) {
      console.error('Bikini set selector: cart-refresh mislukt', err);
    }
  }

  formatPrice(cents) {
    if (typeof cents !== 'number') cents = parseFloat(cents) || 0;
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: this.moneyFormat,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }
}

function initBikiniSetSelectors() {
  document.querySelectorAll('.bikini-set-selector').forEach((el) => {
    if (el.dataset.initialized) return;
    el.dataset.initialized = 'true';
    new BikiniSetSelector(el);
  });
}

document.addEventListener('DOMContentLoaded', initBikiniSetSelectors);
if (document.readyState === 'complete' || document.readyState === 'interactive') {
  initBikiniSetSelectors();
}
